import React from "react";
import { Link } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import { SiShopify, SiReact, SiNodedotjs } from "react-icons/si";
import { HiOutlineSparkles } from "react-icons/hi2";
import useReveal from "../../hooks/useReveal";
import useSEO, { SITE_URL } from "../../seo/useSEO";
import { breadcrumb } from "../../seo/schemas";

const SERVICES = [
  {
    slug: "shopify-development",
    icon: <SiShopify />,
    title: "Shopify Development",
    desc: "Custom Liquid themes, Plus-grade storefronts, headless React fronts and Shopify apps. Built for speed, conversion and editor sanity.",
    tags: ["Liquid", "Shopify Plus", "Hydrogen", "App Dev"],
  },
  {
    slug: "web-apps",
    icon: <SiReact />,
    title: "Full-Stack Web Apps",
    desc: "Production-grade React, Next.js and React Native apps. Strong UX, accessibility, design systems that don't decay.",
    tags: ["React", "Next.js", "TypeScript", "Design Systems"],
  },
  {
    slug: "api-backend",
    icon: <SiNodedotjs />,
    title: "API & Backend",
    desc: "Node.js, Express, Laravel and PHP backends with MongoDB, MySQL and Elasticsearch. Clean APIs, sane data models.",
    tags: ["Node.js", "Laravel", "MongoDB", "Elasticsearch"],
  },
  {
    slug: "performance-seo",
    icon: <HiOutlineSparkles />,
    title: "Performance & SEO",
    desc: "Core Web Vitals, structured data, schema markup, technical SEO. Sub-second TTFB and clean Lighthouse runs.",
    tags: ["Web Vitals", "Schema", "Lighthouse", "Caching"],
  },
];

export default function ServicesIndex() {
  useReveal();
  useSEO({
    title: "Services — Oguzhan Ozcelik | Shopify, Web Apps, API, SEO",
    description:
      "Services from Oguzhan Ozcelik: Shopify development, full-stack web apps, API & backend, performance & SEO. Built in production, shipped to last.",
    path: "/services",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          "@id": `${SITE_URL}/services#list`,
          name: "Services — Oguzhan Ozcelik",
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/services/${s.slug}`,
            name: s.title,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ]),
      ],
    },
    jsonLdId: "services-index-jsonld",
  });

  return (
    <main>
      <section className="section--hero">
        <div className="container-x reveal">
          <span className="eyebrow">What I do</span>
          <h1 className="about-hero__title" style={{ marginTop: 10 }}>
            Services
            <br />
            <span className="it text-accent">that ship.</span>
          </h1>
          <p className="lead" style={{ maxWidth: "62ch", marginTop: 18 }}>
            Four core practices, one obsession: shipping work that performs in
            production — not just looks nice in a Figma frame.
          </p>
        </div>
      </section>

      <section className="section section--tight">
        <div className="container-x">
          <div className="grid grid-2">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="service reveal"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div className="service__icon">{s.icon}</div>
                <h3 className="service__title">{s.title}</h3>
                <p className="service__desc">{s.desc}</p>
                <div className="service__tags" style={{ marginBottom: 14 }}>
                  {s.tags.map((t) => <span key={t}>{t}</span>)}
                </div>
                <span className="chip" style={{ alignSelf: "flex-start" }}>
                  Learn more <HiArrowUpRight />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
